import { ArrowLeftIcon, PencilSimpleIcon, PiggyBankIcon } from '@phosphor-icons/react'
import { type PlainDate, formatMoney, isAfter, isPlainDate, todayIn } from '@paybae/shared'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { Donut, type DonutSegment, chartColor } from '@/components/Donut'
import { ErrorState, Skeleton } from '@/components/Estado'
import {
  AvisoError,
  Campo,
  FormSheet,
  Guardar,
  Monto,
  type OpcionSegmento,
  Segmentos,
} from '@/components/Form'
import { ApiError, type Account, type AllocationPreview, type IncomeEvent } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import {
  useAccounts,
  useAhorroDelIngreso,
  useConfirmarIngreso,
  useIngreso,
  useRepartoPropuesto,
} from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * Confirmar la quincena (§4 y §6).
 *
 * Es el momento en que la app deja de proyectar y empieza a saber: hasta que
 * alguien contesta «llegó tanto», todo lo que enseña el tablero son suposiciones.
 * Por eso el flujo pregunta en el orden en que la realidad se descubre —primero
 * si llegó, luego de cuánto, y solo entonces qué hacer con ello— y no en el
 * orden en que resultaría cómodo guardarlo.
 *
 * Tres decisiones sostienen la pantalla:
 *
 * 1. **En un ingreso variable el monto no se prellena.** El sugerido de una
 *    comisión es una suposición, y un campo ya lleno se confirma sin mirar: la
 *    app acabaría guardando su propia estimación como si fuera un hecho. El fijo
 *    sí se prellena, porque ahí el esperado es el dato real casi siempre y
 *    tecleárselo entero sería trabajo inventado.
 *
 * 2. **El monto se confirma al salir del paso 2, no al final.** Si alguien
 *    abandona en el paso del ahorro, lo importante —cuánto entró— ya quedó
 *    guardado. Al revés, cerrar la pantalla borraría la única respuesta que el
 *    calendario no puede deducir solo.
 *
 * 3. **La diferencia contra lo esperado va en tinta suave.** «$400 menos de lo
 *    previsto» es un dato para decidir el resto de la quincena, no un regaño;
 *    pintarlo de rojo convertiría una nómina normal en una alarma quincenal.
 */

/** Centinela del selector de cuenta: `Segmentos` trabaja con cadenas, no con nulos. */
const SIN_CUENTA = 'sin-cuenta'

type Paso = 'llegada' | 'monto' | 'ahorro'
type Llegada = 'si' | 'no'
type EleccionAhorro = 'objetivo' | 'mitad' | 'nada' | 'otro'

export function ConfirmarIngreso() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { membership } = useSession()

  const evento = useIngreso(id)
  const cuentas = useAccounts()

  const cerrar = (): void => {
    void navigate('/')
  }

  // Se espera a las dos consultas aunque las cuentas solo hagan falta en el
  // segundo paso: dibujar el selector a media hoja movería el botón de guardar
  // justo cuando el dedo ya iba hacia él.
  if (evento.isPending || cuentas.isPending) {
    return <HojaCargando onCerrar={cerrar} />
  }

  const fallo = evento.error ?? cuentas.error
  if (fallo || !evento.data || !cuentas.data) {
    return (
      <HojaDeAviso onCerrar={cerrar}>
        <ErrorState
          titulo="No pudimos cargar este ingreso"
          mensaje={fallo instanceof Error ? fallo.message : 'Inténtalo de nuevo.'}
          onReintentar={() => {
            void evento.refetch()
            void cuentas.refetch()
          }}
        />
      </HojaDeAviso>
    )
  }

  return (
    <Flujo
      evento={evento.data}
      // Los vales de despensa sí pueden recibir un depósito: así es como se
      // llena esa bolsa. Lo que la regla de vales limita es en qué se gastan.
      cuentas={cuentas.data.filter((c) => !c.archivedAt)}
      hoy={todayIn(membership.household.timezone)}
      onCerrar={cerrar}
    />
  )
}

function Flujo({
  evento,
  cuentas,
  hoy,
  onCerrar,
}: {
  evento: IncomeEvent
  cuentas: Account[]
  hoy: PlainDate
  onCerrar: () => void
}) {
  const confirmar = useConfirmarIngreso()
  const decidirAhorro = useAhorroDelIngreso()

  const [paso, setPaso] = useState<Paso>(evento.status === 'CONFIRMED' ? 'ahorro' : 'llegada')
  const [llegada, setLlegada] = useState<Llegada>(evento.status === 'SKIPPED' ? 'no' : 'si')

  // Aquí vive la decisión 1 del encabezado: el fijo arranca con lo esperado, el
  // variable en blanco. Si ya se confirmó antes, manda lo que se guardó — se
  // entra a corregir, no a capturar de nuevo.
  const [montoCents, setMontoCents] = useState(
    evento.actualAmountCents ?? (evento.kind === 'FIXED' ? evento.expectedAmountCents : 0),
  )
  const [fecha, setFecha] = useState<string>(evento.actualDate ?? evento.expectedDate)
  const [cuentaId, setCuentaId] = useState<string>(cuentaSugerida(cuentas, evento.memberId))

  // Quien ya contestó cuánto apartaba vuelve a ver su respuesta, no la nuestra:
  // arrancar en «el objetivo» dejaría el botón listo para pisar una decisión
  // que alguien ya tomó, sin que la pantalla lo mencione.
  const [eleccion, setEleccion] = useState<EleccionAhorro>(
    evento.savings.decidedCents === null ? 'objetivo' : 'otro',
  )
  const [otroCents, setOtroCents] = useState(evento.savings.decidedCents ?? 0)
  const [intentado, setIntentado] = useState(false)

  // El reparto solo se pide en el último paso: pedirlo antes gastaría una ida al
  // servidor en una pantalla que puede cerrarse en el primero.
  const reparto = useRepartoPropuesto(evento.id, paso === 'ahorro')

  // Lo que hay para repartir es lo que se acaba de capturar. El servidor rechaza
  // apartar más que eso, así que el objetivo se acota aquí y no en el reintento.
  const recibidoCents = montoCents
  const objetivoCents = Math.min(evento.savings.targetCents, recibidoCents)
  const objetivoAcotado = evento.savings.targetCents > recibidoCents

  const ahorroCents =
    eleccion === 'objetivo'
      ? objetivoCents
      : eleccion === 'mitad'
        ? Math.round(objetivoCents / 2 / 100) * 100
        : eleccion === 'nada'
          ? 0
          : otroCents

  const fechaValida = isPlainDate(fecha) && !isAfter(fecha, hoy)
  const ahorroExcedido = eleccion === 'otro' && otroCents > recibidoCents

  const errorMonto =
    intentado && paso === 'monto' && montoCents <= 0
      ? 'Escribe cuánto llegó. Si no llegó nada, vuelve atrás y márcalo como no recibido.'
      : undefined
  const errorFecha =
    !isPlainDate(fecha)
      ? 'Escribe la fecha como AAAA-MM-DD.'
      : isAfter(fecha, hoy)
        ? 'Esa fecha todavía no llega.'
        : undefined
  const errorAhorro = ahorroExcedido
    ? `No puedes apartar más de lo que llegó: ${formatMoney(recibidoCents, { hideZeroCents: true })}.`
    : undefined

  const guardando = confirmar.isPending || decidirAhorro.isPending
  const errorServidor = confirmar.error ?? decidirAhorro.error

  /** «Esta vez no llegó»: se registra el hecho y se cierra. No hay más que preguntar. */
  const marcarNoRecibida = (): void => {
    if (guardando) return
    confirmar.mutate(
      { id: evento.id, received: false },
      {
        onSuccess: () => {
          haptic('success')
          onCerrar()
        },
        onError: () => haptic('error'),
      },
    )
  }

  const confirmarMonto = (): void => {
    setIntentado(true)
    if (montoCents <= 0 || !fechaValida) {
      haptic('error')
      return
    }
    confirmar.mutate(
      {
        id: evento.id,
        received: true,
        actualAmountCents: montoCents,
        actualDate: fecha as PlainDate,
        // El nombre importa: el servidor descarta en silencio lo que no
        // reconoce, así que un `accountId` respondería 200 sin mover un peso.
        depositToAccountId: cuentaId === SIN_CUENTA ? null : cuentaId,
      },
      {
        onSuccess: () => {
          haptic('success')
          setIntentado(false)
          setPaso('ahorro')
        },
        onError: () => haptic('error'),
      },
    )
  }

  const guardarAhorro = (): void => {
    if (ahorroExcedido || guardando) {
      haptic('error')
      return
    }
    decidirAhorro.mutate(
      { id: evento.id, savedCents: ahorroCents },
      {
        onSuccess: () => {
          haptic('success')
          onCerrar()
        },
        onError: () => haptic('error'),
      },
    )
  }

  const accion =
    paso === 'llegada'
      ? llegada === 'si'
        ? { onClick: () => setPaso('monto'), etiqueta: 'Anotar cuánto llegó', desactivado: false }
        : { onClick: marcarNoRecibida, etiqueta: 'Marcar que no llegó', desactivado: false }
      : paso === 'monto'
        ? {
            onClick: confirmarMonto,
            etiqueta: montoCents <= 0 ? 'Escribe cuánto llegó' : 'Confirmar el depósito',
            desactivado: false,
          }
        : {
            onClick: guardarAhorro,
            etiqueta: ahorroCents > 0
              ? `Apartar ${formatMoney(ahorroCents, { hideZeroCents: true })}`
              : 'No apartar nada esta vez',
            desactivado: ahorroExcedido,
          }

  return (
    <FormSheet
      tinte="butter"
      titulo={paso === 'ahorro' ? '¿Cuánto vas a ahorrar?' : `Confirmar ${evento.sourceName}`}
      onCerrar={onCerrar}
      accion={
        <div className="flex flex-col gap-2.5">
          <AvisoError mensaje={errorServidor ? mensajeDeError(errorServidor) : null} />
          <Guardar
            onClick={accion.onClick}
            disabled={accion.desactivado}
            guardando={guardando}
          >
            {accion.etiqueta}
          </Guardar>
        </div>
      }
    >
      {/* En el tercer paso no hay flecha de volver: el monto ya quedó guardado,
          y ofrecer «atrás» sugeriría que se puede deshacer. Lo que se puede es
          corregirlo, y para eso está el botón que lo dice con esa palabra. */}
      <Pasos
        actual={paso === 'llegada' ? 1 : paso === 'monto' ? 2 : 3}
        onAtras={paso === 'monto' ? () => setPaso('llegada') : undefined}
      />

      {paso === 'llegada' ? (
        <>
          <ResumenNomina evento={evento} />
          <Campo
            label="¿Ya te llegó?"
            ayuda={
              evento.kind === 'VARIABLE'
                ? 'Un ingreso variable no siempre cae, y hasta que lo confirmes no cuenta para tu disponible.'
                : 'Si marcas que no llegó, tu disponible deja de contar este ingreso.'
            }
          >
            <Segmentos
              opciones={OPCIONES_LLEGADA}
              value={llegada}
              onChange={setLlegada}
            />
          </Campo>
        </>
      ) : null}

      {paso === 'monto' ? (
        <>
          <Campo label="¿Cuánto llegó?" error={errorMonto}>
            <Monto cents={montoCents} onChange={setMontoCents} />
          </Campo>

          {/* En cuanto el monto se separa de lo esperado. En tinta suave: es un
              dato para decidir el resto de la quincena, no un juicio. */}
          <Diferencia evento={evento} montoCents={montoCents} />

          <Campo label="¿Qué día cayó?" error={errorFecha}>
            <input
              type="date"
              value={fecha}
              max={hoy}
              onChange={(e) => setFecha(e.target.value)}
              className="min-h-[48px] w-full rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink"
            />
          </Campo>

          {cuentas.length > 0 ? (
            <Campo
              label="¿A qué cuenta entró?"
              ayuda="Con cuenta subimos su saldo y queda registrado como movimiento. Sin ella solo anotamos que llegó."
            >
              <Segmentos
                opciones={opcionesCuenta(cuentas)}
                value={cuentaId}
                onChange={setCuentaId}
              />
            </Campo>
          ) : (
            <p className="rounded-card bg-surface-sunken px-3.5 py-3 text-[12px] leading-snug text-ink-soft">
              Todavía no tienes cuentas. Crea una desde Ajustes y el próximo depósito se sumará
              solo a su saldo, sin que tengas que capturarlo.
            </p>
          )}
        </>
      ) : null}

      {paso === 'ahorro' ? (
        <>
          <ConfirmadoResumen
            evento={evento}
            montoCents={montoCents}
            onCorregir={() => setPaso('monto')}
          />

          <div className="rounded-card bg-primary-soft px-3.5 py-3">
            <p className="text-[13px] leading-snug text-ink">
              Tu objetivo de este ingreso es apartar{' '}
              <strong data-money className="font-semibold">
                {formatMoney(objetivoCents, { hideZeroCents: true })}
              </strong>
              .
            </p>
            {objetivoAcotado ? (
              // El objetivo sale del porcentaje aplicado a lo esperado. Si entró
              // menos, ese objetivo ya no cabe: decirlo evita un rechazo del
              // servidor que se leería como un fallo de la app.
              <p className="mt-1 text-[12px] leading-snug text-ink-soft">
                Eran {formatMoney(evento.savings.targetCents, { hideZeroCents: true })} sobre lo
                previsto, pero esta vez entró menos.
              </p>
            ) : null}
            {evento.savings.contributedCents > 0 ? (
              <p className="mt-1 text-[12px] leading-snug text-ink-soft">
                De este ingreso ya llevas{' '}
                <strong data-money className="font-semibold text-ink">
                  {formatMoney(evento.savings.contributedCents, { hideZeroCents: true })}
                </strong>{' '}
                aportados a tus fondos.
              </p>
            ) : null}
          </div>

          {/* El reparto va antes de la decisión, no después: enseña a dónde iría
              el dinero mientras todavía se puede elegir cuánto. */}
          <RepartoPropuesto consulta={reparto} />

          <Campo
            label="¿Cuánto vas a apartar?"
            ayuda="Aquí decides el monto; a qué fondo va cada peso se registra desde Ahorro."
            error={eleccion === 'otro' ? undefined : errorAhorro}
          >
            <Segmentos
              opciones={opcionesAhorro(objetivoCents)}
              value={eleccion}
              onChange={setEleccion}
            />
          </Campo>

          {eleccion === 'otro' ? (
            <Campo label="Escribe el monto" error={errorAhorro}>
              <Monto cents={otroCents} onChange={setOtroCents} />
            </Campo>
          ) : null}
        </>
      ) : null}
    </FormSheet>
  )
}

const OPCIONES_LLEGADA: ReadonlyArray<OpcionSegmento<Llegada>> = [
  { value: 'si', label: 'Sí, ya cayó', detalle: 'Anotamos de cuánto fue' },
  { value: 'no', label: 'Todavía no', detalle: 'Esta vez no llegó' },
]

/**
 * En qué paso vamos.
 *
 * Tres puntos y no una barra: es la misma forma redonda con la que se lee todo
 * lo demás en la app, y con tres pasos una barra no aporta precisión que un
 * punto no dé ya.
 */
function Pasos({ actual, onAtras }: { actual: 1 | 2 | 3; onAtras?: () => void }) {
  return (
    <div className="flex min-h-[44px] items-center gap-3">
      {onAtras ? (
        <button
          type="button"
          onClick={() => {
            haptic('light')
            onAtras()
          }}
          aria-label="Volver al paso anterior"
          className="flex size-11 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-ink-soft active:scale-95"
        >
          <ArrowLeftIcon size={18} weight="bold" />
        </button>
      ) : null}

      <span className="text-[12px] font-medium text-ink-soft">Paso {actual} de 3</span>

      <span className="ml-auto flex items-center gap-1.5" aria-hidden>
        {([1, 2, 3] as const).map((n) => (
          <span
            key={n}
            className={cn(
              'rounded-full',
              n === actual ? 'size-2.5 bg-primary' : n < actual ? 'size-2 bg-primary/40' : 'size-2 bg-surface-sunken',
            )}
          />
        ))}
      </span>
    </div>
  )
}

/** De qué nómina hablamos: fuente, día previsto y monto esperado. */
function ResumenNomina({ evento }: { evento: IncomeEvent }) {
  return (
    <div className="rounded-card border border-line bg-surface p-3.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate text-[15px] font-semibold text-ink">
          {evento.sourceName}
        </span>
        <span className="shrink-0 font-mono text-[12px] text-ink-soft">
          {formatoLargo(evento.expectedDate)}
        </span>
      </div>
      <p className="mt-1 text-[13px] leading-snug text-ink-soft">
        {evento.kind === 'FIXED' ? 'Esperabas' : 'Estimabas'}{' '}
        <strong data-money className="font-semibold text-ink">
          {formatMoney(evento.expectedAmountCents, { hideZeroCents: true })}
        </strong>
        {evento.kind === 'VARIABLE' ? ' · es un cálculo nuestro, no una promesa' : ''}
      </p>
    </div>
  )
}

/** «$400 menos de lo previsto». Solo cuando el monto se separa de lo esperado. */
function Diferencia({ evento, montoCents }: { evento: IncomeEvent; montoCents: number }) {
  const delta = montoCents - evento.expectedAmountCents
  if (montoCents <= 0 || delta === 0) return null

  return (
    <p className="text-[13px] leading-snug text-ink-soft">
      <strong data-money className="font-semibold">
        {formatMoney(Math.abs(delta), { hideZeroCents: true })}
      </strong>{' '}
      {delta > 0 ? 'más' : 'menos'} de lo {evento.kind === 'FIXED' ? 'previsto' : 'estimado'}.
    </p>
  )
}

/** Lo que quedó confirmado, con la puerta abierta a corregirlo. */
function ConfirmadoResumen({
  evento,
  montoCents,
  onCorregir,
}: {
  evento: IncomeEvent
  montoCents: number
  onCorregir: () => void
}) {
  return (
    <div className="flex items-center gap-3 rounded-card border border-line bg-surface p-3.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] text-ink-soft">Llegó de {evento.sourceName}</p>
        <p data-money className="font-display text-[24px] font-extrabold leading-tight tracking-[-0.03em] text-ink">
          {formatMoney(montoCents, { hideZeroCents: true })}
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          haptic('light')
          onCorregir()
        }}
        className="flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-pill border border-line bg-surface px-3.5 text-[13px] font-semibold text-ink active:scale-95"
      >
        <PencilSimpleIcon size={15} weight="bold" />
        Corregir
      </button>
    </div>
  )
}

/**
 * A qué fondo iría cada peso, según el reparto que ya está configurado.
 *
 * Es una propuesta, no una aportación: aquí solo se decide **cuánto** apartar.
 * La dona muestra el reparto como parte de un todo cerrado, que es la pregunta
 * real —«de lo que voy a ahorrar, ¿qué proporción va a cada fondo?»—.
 */
function RepartoPropuesto({
  consulta,
}: {
  consulta: ReturnType<typeof useRepartoPropuesto>
}) {
  if (consulta.isPending) {
    return (
      <div className="flex items-center gap-4 rounded-card border border-line bg-surface p-3.5">
        <Skeleton className="size-[120px] rounded-full" />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton className="h-3.5 w-full" />
          <Skeleton className="h-3.5 w-4/5" />
        </div>
      </div>
    )
  }

  // Un fallo del reparto no bloquea la decisión: se puede apartar dinero sin
  // saber a qué fondo irá. Se calla en vez de interrumpir con un error rojo.
  if (consulta.isError || !consulta.data) return null

  const preview: AllocationPreview = consulta.data

  if (preview.lines.length === 0) {
    return (
      <p className="rounded-card bg-surface-sunken px-3.5 py-3 text-[12px] leading-snug text-ink-soft">
        Todavía no repartes tu ahorro entre fondos. Asígnale un porcentaje a cada meta desde
        Ahorro y la próxima vez te diremos aquí cuánto le toca a cada uno.
      </p>
    )
  }

  // De mayor a menor: la rampa de gráficas reserva los colores distintivos para
  // los primeros índices, así que los tramos grandes tienen que ir delante.
  const lineas = [...preview.lines].sort((a, b) => b.amountCents - a.amountCents)
  const segmentos: DonutSegment[] = lineas.map((linea, i) => ({
    key: linea.goalId,
    label: linea.name,
    value: linea.amountCents,
    color: chartColor(i),
  }))

  return (
    <section className="flex flex-col gap-3 rounded-card border border-line bg-surface p-3.5">
      <header className="flex items-center gap-2">
        <PiggyBankIcon size={16} weight="fill" className="shrink-0 text-ink-soft" />
        <h2 className="text-[14px] font-semibold text-ink">Reparto propuesto</h2>
      </header>

      <div className="flex items-center gap-4">
        <Donut
          segments={segmentos}
          centerValue={formatMoney(preview.totalCents, { hideZeroCents: true })}
          centerLabel={`${preview.totalPercent}% a fondos`}
        />
        <ul className="flex min-w-0 flex-1 flex-col gap-2">
          {lineas.map((linea, i) => (
            <li key={linea.goalId} className="flex items-baseline gap-2">
              <span
                aria-hidden="true"
                className="size-2 shrink-0 translate-y-[-1px] rounded-full"
                style={{ background: chartColor(i) }}
              />
              <span className="line-clamp-2 min-w-0 text-[13px] leading-tight text-ink-soft">
                {linea.name}
              </span>
              <span className="ml-auto shrink-0 text-right">
                <span data-money className="block text-[13px] font-semibold text-ink">
                  {formatMoney(linea.amountCents, { hideZeroCents: true })}
                </span>
                <span className="block font-mono text-[11px] text-ink-faint">{linea.percent}%</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <p className="text-[12px] leading-snug text-ink-faint">
        Después del reparto te quedarían{' '}
        <strong data-money className="font-semibold text-ink-soft">
          {formatMoney(preview.remainderCents, { hideZeroCents: true })}
        </strong>{' '}
        para el resto del periodo.
      </p>
    </section>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Piezas sueltas
// ═══════════════════════════════════════════════════════════════════════════

function opcionesAhorro(objetivoCents: number): ReadonlyArray<OpcionSegmento<EleccionAhorro>> {
  const mitad = Math.round(objetivoCents / 2 / 100) * 100
  return [
    {
      value: 'objetivo',
      label: 'El objetivo',
      detalle: formatMoney(objetivoCents, { hideZeroCents: true }),
    },
    { value: 'mitad', label: 'La mitad', detalle: formatMoney(mitad, { hideZeroCents: true }) },
    { value: 'nada', label: 'Nada', detalle: 'Esta vez no' },
    { value: 'otro', label: 'Otro monto', detalle: 'Lo escribo yo' },
  ]
}

function opcionesCuenta(cuentas: readonly Account[]): ReadonlyArray<OpcionSegmento<string>> {
  return [
    { value: SIN_CUENTA, label: 'Ninguna', detalle: 'Solo anótalo' },
    ...cuentas.map((c) => ({
      value: c.id,
      label: c.name,
      detalle: `${formatMoney(c.balanceCents, { hideZeroCents: true })}${
        c.owner ? ` · ${nombreCorto(c.owner.user.displayName)}` : ' · Común'
      }`,
    })),
  ]
}

/**
 * A qué cuenta suele caer esta nómina.
 *
 * Solo se preselecciona cuando no hay ambigüedad —una única cuenta de débito de
 * quien cobra—. Con dos candidatas se deja en blanco: elegir por la persona
 * subiría el saldo de una cuenta que nadie señaló, y un saldo que se mueve solo
 * es la clase de sorpresa que hace desconfiar de toda la app.
 */
function cuentaSugerida(cuentas: readonly Account[], memberId: string): string {
  const propias = cuentas.filter((c) => c.ownerMemberId === memberId && c.type === 'CHECKING')
  return propias.length === 1 ? propias[0]!.id : SIN_CUENTA
}

/** El detalle por campo del servidor dice qué corregir; «revisa los datos» no. */
function mensajeDeError(error: unknown): string {
  if (error instanceof ApiError) {
    const detalles = Object.values(error.fieldErrors ?? {}).flat()
    return detalles.length > 0 ? detalles.join('. ') : error.message
  }
  return 'No pudimos guardar. Inténtalo de nuevo.'
}

function nombreCorto(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? displayName
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

function formatoLargo(date: PlainDate): string {
  const [, month, day] = date.split('-')
  return `${Number(day)} de ${MESES[Number(month) - 1]}`
}

/** Mismo esqueleto que el primer paso, para que nada salte al llegar los datos. */
function HojaCargando({ onCerrar }: { onCerrar: () => void }) {
  return (
    <FormSheet
      tinte="butter"
      titulo="Confirmar ingreso"
      onCerrar={onCerrar}
      accion={
        <Guardar onClick={() => undefined} disabled>
          Cargando…
        </Guardar>
      }
    >
      <Skeleton className="h-[44px]" />
      <Skeleton className="h-[82px]" />
      <Skeleton className="h-[128px]" />
    </FormSheet>
  )
}

function HojaDeAviso({
  onCerrar,
  children,
}: {
  onCerrar: () => void
  children: React.ReactNode
}) {
  return (
    <FormSheet
      tinte="butter"
      titulo="Confirmar ingreso"
      onCerrar={onCerrar}
      accion={<Guardar onClick={onCerrar}>Volver al inicio</Guardar>}
    >
      {children}
    </FormSheet>
  )
}
